import { Component, EventEmitter, Input, Output, ViewChild, TemplateRef, ElementRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BaseComponent, DrawerService } from '@portals/core';
import { NgImgEditorService, NgxCroppedEvent } from 'ng-img-editor';
import { tap } from 'rxjs';
import { DataUrl } from 'ngx-image-compress';

@Component({
	selector: 'portals-image-uploader',
	templateUrl: './image-uploader.component.html',
	styleUrls: ['./image-uploader.component.scss'],
})
export class ImageUploaderComponent extends BaseComponent {
	@Input() image?: string;
	@Input() imageUpload!: any;
	@Input() aspectRatio!: number;
	@Input() minRanges!: { height: number; width: number };
	@Input() imageResize!: { resizeToHeight?: number; resizeToWidth?: number };
	@Input() imageData: any;
	@Output() imageEmitter = new EventEmitter();
	@ViewChild('createEventFileInput', { static: false }) createEventFileInput!: ElementRef;
	@ViewChild('fileUploadExceedDialog', { static: true })
	fileUploadExceedDialog!: TemplateRef<any>;

	constructor(public drawer: DrawerService<Record<string, unknown>>, public dialog: MatDialog, private editorService: NgImgEditorService) {
		super();
	}

	clearImageOnClick() {
		this.createEventFileInput.nativeElement.value = '';
		this.image = '';
		this.imageUpload = null;
		this.imageData = null;
		this.imageEmitter.emit(null);
	}

	async onImageChanged(event: Event) {
		let file = event.target instanceof HTMLInputElement && event.target.files ? event.target.files[0] : null;

		if (!file) {
			return;
		}

		if (file.size > 10 * 1024 * 1024) {
			this.dialog.open(this.fileUploadExceedDialog, {
				disableClose: true,
				panelClass: 'file-upload-exceed-dialog',
				autoFocus: false,
				restoreFocus: false,
			});
			return;
		}

		let url = URL.createObjectURL(file);
		const dimensions: { width: number; height: number } = await this.getHeightAndWidthFromDataUrl(url);
		let resizeOptions: { resizeToHeight?: number; resizeToWidth?: number } = {};

		if (!this.imageResize && this.minRanges) {
			if (dimensions.height <= dimensions.width && dimensions.height < this.minRanges.height) {
				resizeOptions.resizeToHeight = this.minRanges.height;
				resizeOptions.resizeToWidth = dimensions.width * (this.minRanges.height / dimensions.height);
			} else if (dimensions.width <= dimensions.height && dimensions.width < this.minRanges.width) {
				resizeOptions.resizeToWidth = this.minRanges.width;
				resizeOptions.resizeToHeight = dimensions.height * (this.minRanges.width / dimensions.width);
			}
		} else if (this.imageResize) {
			resizeOptions = this.imageResize;
			if (!resizeOptions.resizeToWidth && resizeOptions.resizeToHeight) resizeOptions.resizeToWidth = dimensions.width * (resizeOptions.resizeToHeight / dimensions.height);
			else if (!resizeOptions.resizeToHeight && resizeOptions.resizeToWidth) resizeOptions.resizeToHeight = dimensions.height * (resizeOptions.resizeToWidth / dimensions.width);
		}

		this.subscribe(
			this.editorService
				.open(event, {
					aspectRatio: this.aspectRatio,
					...resizeOptions,
				})
				.pipe(
					tap((data: NgxCroppedEvent) => {
						this.image = data.base64;
						this.imageData = data;
						this.imageEmitter.emit({
							imageToUpload: this.imageData.file,
							imageToView: this.imageData.base64,
						});
					})
				)
		);
	}

	getHeightAndWidthFromDataUrl(dataURL: DataUrl): Promise<{ width: number; height: number }> {
		return new Promise((resolve) => {
			const img = new Image();
			img.onload = () => {
				resolve({
					height: img.height,
					width: img.width,
				});
			};
			img.src = dataURL;
		});
	}

	protected readonly console = console;
}
